import React from "react";
import { BODY_OUTLINE, VIEWS, zoneColor } from "@/components/booking/bodyZones";

// Looping "GIF-like" animations (CSS only — crisp, no downloads)
const KEYFRAMES = `
@keyframes azRise{0%,100%{opacity:0;transform:translateY(9px)}8%,88%{opacity:1;transform:translateY(0)}}
@keyframes azPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.35)}}
@keyframes azGlow{0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.55)}60%{box-shadow:0 0 0 7px rgba(220,38,38,0)}}
@keyframes azBar{0%,100%{transform:scaleX(0)}12%,90%{transform:scaleX(1)}}
@keyframes azToggle{0%,45%{transform:translateX(0)}55%,100%{transform:translateX(12px)}}
@keyframes azToggleTrack{0%,45%{background:hsl(var(--muted))}55%,100%{background:#3D5A45}}
@keyframes azFade{0%{opacity:0;transform:translateY(10px) scale(.99)}100%{opacity:1;transform:none}}
@media (prefers-reduced-motion: reduce){*{animation:none!important}}
`;
export const ShotStyles = () => <style>{KEYFRAMES}</style>;
const rise = (i = 0) => ({ animation: `azRise 6s ${(i * 0.45).toFixed(2)}s ease-in-out infinite` });

const Frame = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-card shadow-xl overflow-hidden">
    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-muted/40">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      <span className="ml-3 text-xs text-muted-foreground font-medium">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Chip = ({ children, tone = "primary", style }) => {
  const tones = { primary: "bg-primary/10 text-primary", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700", green: "bg-emerald-100 text-emerald-700", violet: "bg-violet-100 text-violet-700" };
  return <span style={style} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
};

export const AssistantShot = () => (
  <Frame title="AI Assistant">
    <div className="flex justify-end mb-2" style={rise(0)}>
      <div className="bg-primary text-primary-foreground rounded-2xl px-3 py-1.5 text-[11px] max-w-[80%]">Book Emma in for laser Tue 2pm & text her the aftercare</div>
    </div>
    <div className="flex justify-start mb-2" style={rise(1)}>
      <div className="bg-muted rounded-2xl px-3 py-1.5 text-[11px] max-w-[85%]">On it — here's what I'll do 👇</div>
    </div>
    <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-2.5 mb-2" style={rise(2)}>
      <div className="text-[9px] font-bold uppercase tracking-widest text-primary mb-1">Confirm action</div>
      <div className="text-[11px] leading-snug">Book <b>Underarm Laser</b> with <b>Ava</b> for <b>Emma</b>, Tue 2:00 PM · then SMS aftercare link</div>
      <div className="flex gap-1.5 mt-2">
        <span className="text-[10px] font-semibold px-2.5 py-1 rounded-md bg-primary text-primary-foreground">Confirm</span>
        <span className="text-[10px] font-semibold px-2.5 py-1 rounded-md border border-border">Cancel</span>
      </div>
    </div>
    <div className="flex gap-1.5 flex-wrap">
      {["Book", "Send aftercare", "Add task", "SMS a segment"].map((l, i) => <Chip key={l} style={rise(i + 3)}>{l}</Chip>)}
    </div>
  </Frame>
);

export const PricingShot = () => (
  <Frame title="True-cost pricing — Dermal Filler 1ml">
    <div className="flex items-end justify-between mb-3" style={rise(0)}>
      <div><div className="text-[10px] text-muted-foreground">You charge</div><div className="font-heading font-bold text-2xl">$550</div></div>
      <div className="text-right"><div className="text-[10px] text-muted-foreground">Profit / hr</div><div className="font-heading font-bold text-2xl text-emerald-600">$372</div></div>
    </div>
    {[["Product & consumables", 145, "#B5704F"], ["Staff time (wage-aware)", 58, "#A38059"], ["Overheads", 24, "#B4B9B2"]].map(([l, v, c], i) => (
      <div key={l} className="mb-1.5" style={rise(i + 1)}>
        <div className="flex justify-between text-[11px]"><span>{l}</span><span>${v}</span></div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v / 3}%`, background: c }} /></div>
      </div>
    ))}
    <div className="flex items-center justify-between mt-3 pt-2 border-t border-border" style={rise(4)}>
      <span className="text-[11px] font-medium">Gross margin</span>
      <Chip tone="green">59%</Chip>
    </div>
  </Frame>
);

export const ReviewsShot = () => (
  <Frame title="Feedback & Google reviews">
    <div className="grid grid-cols-3 gap-2 mb-3">
      {[["Avg rating", "4.8★"], ["Responses", "212"], ["→ Google", "88%"]].map(([l, v], i) => (
        <div key={l} style={rise(i)} className="rounded-lg border border-border p-2 text-center"><div className="text-[9px] text-muted-foreground">{l}</div><div className="font-heading font-bold text-base">{v}</div></div>
      ))}
    </div>
    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-2.5 mb-2" style={rise(3)}>
      <div className="text-[11px] font-medium text-emerald-800">Happy client → invite to Google ⭐⭐⭐⭐⭐</div>
    </div>
    <div className="rounded-xl bg-muted p-2.5" style={rise(4)}>
      <div className="text-[11px] text-muted-foreground">Unhappy feedback stays private for you to fix</div>
    </div>
  </Frame>
);

export const CrmShot = () => (
  <Frame title="CRM — retention">
    <div className="grid grid-cols-2 gap-2 mb-3">
      {[["New", "24", "primary"], ["Active", "186", "green"], ["Lapsed", "41", "amber"], ["VIP", "18", "violet"]].map(([l, v, t], i) => (
        <div key={l} style={rise(i)} className="rounded-lg border border-border p-2 flex items-center justify-between">
          <span className="text-[11px]">{l}</span><Chip tone={t}>{v}</Chip>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between rounded-xl bg-muted p-2.5" style={rise(4)}>
      <span className="text-[11px] font-medium">Auto win-back lapsed clients</span>
      <span className="relative inline-block w-8 h-4 rounded-full" style={{ animation: "azToggleTrack 6s .3s ease-in-out infinite" }}>
        <span className="absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow" style={{ animation: "azToggle 6s .3s ease-in-out infinite" }} />
      </span>
    </div>
  </Frame>
);

export const ClientFileShot = () => (
  <Frame title="Client file — Emma Walker">
    <div className="flex items-center gap-3 mb-3" style={rise(0)}>
      <div className="h-11 w-11 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold">EW</div>
      <div>
        <div className="font-semibold">Emma Walker</div>
        <div className="text-xs text-muted-foreground">Added 2024 · 14 visits · $2,340 spent</div>
      </div>
    </div>
    <div className="flex gap-1.5 mb-3 flex-wrap">
      {[["legacy", "amber"], ["active", "green"], ["facials", "primary"], ["high-spender", "violet"], ["voucher-buyer", "primary"]].map(([l, t], i) => <Chip key={l} tone={t} style={rise(i + 1)}>{l}</Chip>)}
    </div>
    <div className="grid grid-cols-5 gap-1 text-center text-[10px] mb-3">
      {["Details", "Bookings", "Charts", "Forms", "Photos"].map((t, i) => <div key={t} style={rise(i)} className={`py-1.5 rounded-md ${i === 4 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{t}</div>)}
    </div>
    <div className="grid grid-cols-3 gap-1.5">
      {["#F5D0C5", "#E8C4A0", "#D9B99B"].map((c, i) => (
        <div key={i} style={rise(i + 2)} className="rounded-lg h-16 relative overflow-hidden">
          <div className="absolute inset-0" style={{ background: c }} />
          <span className="absolute top-1 left-1 text-[8px] font-bold uppercase px-1 rounded-full bg-white/80">{i === 0 ? "before" : i === 2 ? "after" : "prog"}</span>
        </div>
      ))}
    </div>
  </Frame>
);

export const ReportsShot = () => (
  <Frame title="Sales report — this month">
    <div className="grid grid-cols-3 gap-2 mb-3">
      {[["Takings", "$18,420"], ["New clients", "37"], ["No-shows", "4"]].map(([l, v], i) => (
        <div key={l} style={rise(i)} className="rounded-lg border border-border p-2"><div className="text-[10px] text-muted-foreground">{l}</div><div className="font-heading font-bold text-lg">{v}</div></div>
      ))}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">How clients paid</div>
    {[["Card on file", 62, "#3D5A45"], ["EFTPOS", 28, "#3E4552"], ["Cash", 10, "#B4B9B2"]].map(([l, p, c], i) => (
      <div key={l} className="mb-1.5">
        <div className="flex justify-between text-[11px]"><span>{l}</span><span>{p}%</span></div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p}%`, background: c, transformOrigin: "left", animation: `azBar 6s ${(i * 0.4).toFixed(2)}s ease-in-out infinite` }} /></div>
      </div>
    ))}
  </Frame>
);

export const CheckoutShot = () => (
  <Frame title="Checkout — one-click card on file">
    <div className="space-y-1.5 text-sm">
      {[["Hydrafacial", "$180.00", ""], ["SPF50 serum ×1", "$49.00", ""], ["Legacy client −15%", "−$34.35", "text-amber-600"]].map(([l, v, c], i) => (
        <div key={l} style={rise(i)} className={`flex justify-between ${c}`}><span>{l}</span><span className="tabular-nums">{v}</span></div>
      ))}
      <div className="border-t border-border my-2" />
      <div className="flex justify-between font-heading font-bold text-base" style={rise(3)}><span>Total</span><span className="tabular-nums">$194.65</span></div>
    </div>
    <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-2.5 flex items-center justify-between" style={rise(4)}>
      <span className="text-sm flex items-center gap-2"><span className="font-mono">Visa •••• 4242</span></span>
      <span className="text-xs font-semibold text-primary" style={{ animation: "azPulse 2.4s ease-in-out infinite" }}>Charge card →</span>
    </div>
  </Frame>
);

export const MessagesShot = () => (
  <Frame title="Customer messages — automations">
    {[["Reminder", "green", "on"], ["No-show follow-up", "amber", "on"], ["Rebook prompt", "primary", "off"], ["Review request", "violet", "off"]].map(([l, t, s], i) => (
      <div key={l} style={rise(i)} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0">
        <span className="text-sm flex items-center gap-2"><Chip tone={t}>SMS</Chip>{l}</span>
        <span className={`h-4 w-7 rounded-full relative ${s === "on" ? "bg-emerald-500" : "bg-muted"}`}><span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white ${s === "on" ? "right-0.5" : "left-0.5"}`} /></span>
      </div>
    ))}
    <div className="mt-2 text-[11px] text-muted-foreground bg-muted/50 rounded-lg p-2">“Hi {"{client_name}"}, reminder: your {"{service}"} at {"{clinic}"} is on {"{datetime}"}.”</div>
  </Frame>
);

const MiniBody = () => {
  const on = new Set(["underarm_l", "underarm_r", "lower_leg_l", "lower_leg_r", "knee_l", "knee_r", "foot_l", "foot_r"]);
  return (
    <svg viewBox="0 0 200 440" className="w-full h-full">
      <defs><clipPath id="shot-body-clip"><path d={BODY_OUTLINE} /></clipPath></defs>
      <path d={BODY_OUTLINE} fill="hsl(var(--muted))" stroke="hsl(var(--foreground) / 0.35)" strokeWidth="1.5" strokeLinejoin="round" />
      <g clipPath="url(#shot-body-clip)">{VIEWS.front.zones.map((z) => <path key={z.id} d={z.d} fill={on.has(z.id) ? zoneColor(z.id) : "transparent"} fillOpacity="0.85" stroke={on.has(z.id) ? "#fff" : "hsl(var(--foreground) / 0.08)"} strokeWidth={on.has(z.id) ? 1.4 : 0.75} strokeDasharray={on.has(z.id) ? "" : "2 2"} />)}</g>
      <path d={BODY_OUTLINE} fill="none" stroke="hsl(var(--foreground) / 0.35)" strokeWidth="1.5" />
      <circle cx="80" cy="150" r="7" fill="#3E4552" stroke="#fff" strokeWidth="2" /><text x="80" y="153" textAnchor="middle" fontSize="8" fill="#fff" fontWeight="700">1</text>
    </svg>
  );
};

export const ChartingShot = () => (
  <Frame title="Treatment chart — Laser / Hair">
    <div className="rounded-lg border border-primary/30 bg-primary/5 px-2 py-1.5 text-[10px] mb-2" style={rise(0)}><span className="font-semibold">Pre-filled from last visit</span> · 8 zones with settings, device & skin type carried over</div>
    <div className="flex gap-3">
      <div className="w-24 shrink-0 rounded-lg border border-border bg-card p-1"><MiniBody /></div>
      <div className="flex-1 space-y-1.5 text-[11px] min-w-0">
        <div className="flex gap-1 flex-wrap"><Chip style={rise(1)}>Front</Chip><Chip tone="violet" style={rise(1)}>Back</Chip><Chip style={rise(1)}>Face</Chip><span className="ml-auto text-[10px] text-muted-foreground">Half leg · Underarms</span></div>
        <div className="rounded-lg border border-primary bg-primary/5 p-1.5" style={rise(2)}>
          <div className="font-medium">Left underarm</div>
          <div className="grid grid-cols-5 gap-1 mt-1 text-center">{[["18", "mm"], ["14", "J/cm²"], ["3", "ms"], ["2", "Hz"], ["2", "pass"]].map(([v, u]) => <div key={u} className="rounded border border-border bg-card py-0.5"><div className="font-semibold">{v}</div><div className="text-[8px] text-muted-foreground">{u}</div></div>)}</div>
        </div>
        {[["Device + wavelength", "GentleMax Pro 755"], ["Skin response", "Erythema · Oedema"], ["Last visit", "Open chart →"]].map(([l, v], i) => (
          <div key={l} style={rise(i + 3)} className="flex justify-between border-b border-border/60 py-1 last:border-0"><span className="text-muted-foreground">{l}</span><span className={`font-medium ${l === "Last visit" ? "text-primary" : ""}`}>{v}</span></div>
        ))}
      </div>
    </div>
  </Frame>
);

export const AlertsShot = () => (
  <Frame title="Medical alerts — acknowledge before treatment">
    <div className="rounded-xl border-2 border-red-300 bg-red-50 p-2.5 space-y-1.5">
      <div className="flex items-center gap-1.5 text-red-700 font-semibold text-xs">⚠ Active medical alerts (3)</div>
      {[["Isotretinoin / Roaccutane", "From consultation form"], ["Pregnancy / breastfeeding", "From consultation form"], ["Penicillin allergy", "Added by Ava"]].map(([l, s], i) => (
        <div key={l} style={rise(i)} className="flex items-center gap-2 bg-white/70 border border-red-200 rounded-lg px-2 py-1.5">
          <Chip tone="red">{l}</Chip>
          <span className="text-[10px] text-muted-foreground flex-1 truncate">{s}</span>
          <span className="text-[10px] font-semibold text-white bg-red-600 rounded-md px-1.5 py-0.5" style={{ animation: `azGlow 2.6s ${(i * 0.3).toFixed(2)}s ease-in-out infinite` }}>Acknowledge</span>
        </div>
      ))}
    </div>
    <p className="text-[10px] text-muted-foreground mt-2">Blocked at check-in — treatment can't start until every alert is acknowledged.</p>
  </Frame>
);

export const ReportsHubShot = () => (
  <Frame title="Reports">
    {[["Business performance", ["Executive summary", "Future value", "Retention"]],
      ["Financial", ["Sales", "Service sales", "Transactions"]],
      ["Clients", ["Client overview", "Absent since", "Credits"]]].map(([cat, items], ci) => (
      <div key={cat} className="mb-2.5">
        <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1">{cat}</div>
        <div className="grid grid-cols-3 gap-1.5">
          {items.map((it, i) => <div key={it} style={rise(ci * 3 + i)} className="rounded-lg border border-border px-2 py-1.5 text-[10px] font-medium text-center">{it}</div>)}
        </div>
      </div>
    ))}
  </Frame>
);

export const StaffFileShot = () => (
  <Frame title="Staff file — access & roster">
    <div className="grid grid-cols-3 gap-1 text-center text-[10px] mb-2.5">
      {["Roster", "Access level", "Pay"].map((t, i) => <div key={t} style={rise(i)} className={`py-1.5 rounded-md ${i === 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{t}</div>)}
    </div>
    {[["See sales & reports"], ["Give discounts"], ["Edit client files"]].map(([l], i) => (
      <div key={l} style={rise(i + 1)} className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0">
        <span className="text-[11px]">{l}</span>
        <span className="h-4 w-7 rounded-full relative" style={{ animation: `azToggleTrack 4.5s ${(i * 0.5).toFixed(2)}s ease-in-out infinite` }}>
          <span className="absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white" style={{ animation: `azToggle 4.5s ${(i * 0.5).toFixed(2)}s ease-in-out infinite` }} />
        </span>
      </div>
    ))}
    <div className="mt-2 flex gap-1.5 flex-wrap"><Chip tone="green" style={rise(4)}>Mon–Fri 9–5</Chip><Chip style={rise(5)}>Reception</Chip></div>
  </Frame>
);
